import { useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../supabase';
import { LOCALES, LOCALE_META } from '../lib/locales';

/** First run after signing in: confirm who you are and what you can judge.
 *  This is the only path that creates a member row. */
export default function RequestAccess({ session, onDone }: { session: Session; onDone: () => void }) {
  const meta = session.user.user_metadata ?? {};
  const [name, setName] = useState<string>(meta.full_name ?? meta.name ?? '');
  const [langs, setLangs] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (l: string) =>
    setLangs((cur) => (cur.includes(l) ? cur.filter((x) => x !== l) : [...cur, l]));

  async function submit() {
    if (!name.trim()) return setError('Add your name so your work can be credited.');
    if (!langs.length) return setError('Pick at least one language you can judge.');
    setBusy(true); setError(null);
    const { error } = await supabase.rpc('i18n_request_access', {
      p_display_name: name.trim(), p_languages: langs,
    });
    setBusy(false);
    if (error) return setError(error.message);
    onDone();
  }

  return (
    <div className="gate">
      <div className="mark">Almost there</div>
      <p className="lede">
        Tell us which languages you can judge. Your queue merges them into one list, tagged by
        language, so you never switch tabs mid-session.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div className="field">
          <label htmlFor="nm">Your name</label>
          <input id="nm" className="inp" value={name} onChange={(e) => setName(e.target.value)} />
        </div>

        <div className="field">
          <label>Languages you can judge</label>
          <div className="langpick">
            {LOCALES.map((l) => (
              <button key={l} className="lp" aria-pressed={langs.includes(l)} onClick={() => toggle(l)}>
                <span style={{ flex: 1 }}>
                  <span className="nm2" style={{ display: 'block' }}>{LOCALE_META[l].native}</span>
                  <span className="code">{l}</span>
                </span>
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 9, alignItems: 'center' }}>
          <button className="btn pri" onClick={() => void submit()} disabled={busy}>
            {busy ? 'Requesting…' : 'Request access'}
          </button>
          {error && <span className="hint" style={{ color: 'var(--crit)' }}>{error}</span>}
        </div>
      </div>
    </div>
  );
}
